import { FiX, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: {
    step1?: string;
    step2?: {
      content: string;
      hashtags: string;
    };
    research?: string;
    sequence?: string;
  };
  messageType?: string;
}

const ResultsModal = ({ isOpen, onClose, results, messageType }: ResultsModalProps) => {
  const [currentStep, setCurrentStep] = useState<'step1' | 'step2'>('step1');

  if (!isOpen) return null;

  const formatSocialMediaPost = () => {
    if (currentStep === 'step1') {
      return results.step1 || '';
    } else {
      const step2 = results.step2;
      if (!step2) return '';
      
      return `# Social Media Post Content

${step2.content}

## Hashtags
${step2.hashtags}`;
    }
  };

  const formatResearch = (research: string | undefined) => {
    if (!research) return '';
    try {
      const data = JSON.parse(research);
      let markdown = '';

      // Market Conditions
      if (data.market_conditions?.length) {
        markdown += `# 1. Market Conditions\n${data.market_conditions.map((m: any) => `### ${m.aspect}
${m.details}
\n**Impact on Role**:\n${m.relevance}`).join('\n\n')}\n\n`;
      }

      // Industry Trends
      if (data.trends?.length) {
        markdown += `# 2. Industry Trends\n${data.trends.map((t: any) => `### ${t.name}
${t.description}
\n**Impact**:\n${t.impact}`).join('\n\n')}\n\n`;
      }

      // Pain Points
      if (data.pain_points?.length) {
        markdown += `# 3. Pain Points\n${data.pain_points.map((p: any) => `### ${p.description}
${p.impact}
\n**Urgency**:\n${p.urgency_level}`).join('\n\n')}\n\n`;
      }

      // Decision Maker Profile
      if (data.decision_maker_profile) {
        markdown += `# 4. Decision Maker Profile\n`;
        
        if (data.decision_maker_profile.priorities?.length) {
          markdown += `### Priorities\n${data.decision_maker_profile.priorities.map((p: string) => `- ${p}`).join('\n')}\n\n`;
        }
        
        if (data.decision_maker_profile.challenges?.length) {
          markdown += `### Challenges\n${data.decision_maker_profile.challenges.map((c: string) => `- ${c}`).join('\n')}\n\n`;
        }
        
        if (data.decision_maker_profile.motivations?.length) {
          markdown += `### Motivations\n${data.decision_maker_profile.motivations.map((m: string) => `- ${m}`).join('\n')}\n\n`;
        }
        
        if (data.decision_maker_profile.objections?.length) {
          markdown += `### Common Objections\n${data.decision_maker_profile.objections.map((o: string) => `- ${o}`).join('\n')}\n\n`;
        }
      }

      // Competitive Landscape
      if (data.competitive_landscape?.length) {
        markdown += `# 5. Competitive Landscape\n${data.competitive_landscape.map((c: any) => `### ${c.factor}
${c.description}
\n**Pressure Point**:\n${c.pressure_point}`).join('\n\n')}`;
      }

      return markdown;
    } catch (error) {
      console.error('Error parsing research:', error);
      return 'Error parsing research data';
    }
  };

  const formatSequence = (sequence: string | undefined) => {
    if (!sequence) return '';
    try {
      const data = JSON.parse(sequence);

      // Check for error in sequence
      if (data.error || (data.sequence && data.sequence.error)) {
        const error = data.error || data.sequence.error;
        const details = data.details || data.sequence.details;
        return `# Error Generating Sequence\n\n**Error**: ${error}\n\n**Details**: ${details}`;
      }

      let markdown = '';

      if (messageType === 'LinkedIn DM') {
        // Format LinkedIn DM sequence
        if (data.sequence?.connection_request) {
          markdown += `# Connection Request\n\n`;
          markdown += `${data.sequence.connection_request.message}\n\n`;
          markdown += `**Strategy Note**: ${data.sequence.connection_request.note}\n\n---\n\n`;
        }

        if (data.sequence?.follow_ups?.length) {
          markdown += `# Follow-up Messages\n\n`;
          data.sequence.follow_ups.forEach((msg: any) => {
            markdown += `## Message ${msg.message_number}\n\n`;
            markdown += `**Timing**: ${msg.timing}\n\n`;
            markdown += `${msg.message}\n\n`;
            markdown += `**Strategy**: ${msg.strategy}\n\n---\n\n`;
          });
        }
      } else {
        // Format email sequence
        if (!data.sequence) {
          throw new Error('Invalid sequence format');
        }
        
        return data.sequence.map((email: any) => `# Email ${email.email_number}

${email.variants.map((variant: any) => `### Variant ${variant.variant}
${email.email_number === 1 ? `### Subject Line
${variant.subject_line}

` : ''}### Body
${variant.body
  .replace(/([.!?])\s*/g, '$1\n\n') // Add double newline after each sentence
  .split('\n')
  .filter((line: string) => line.trim().length > 0) // Remove empty lines
  .join('\n')}`).join('\n\n')}`).join('\n\n---\n\n');
      }

      return markdown;
    } catch (error) {
      console.error('Error parsing sequence:', error);
      return '# Error\n\nFailed to parse sequence data. Please try again.';
    }
  };

  const handleCopyToClipboard = () => {
    let content = '';
    if (currentStep === 'step1' && results.research) {
      content = results.research;
    } else if (currentStep === 'step2' && results.sequence) {
      content = results.sequence;
    } else if (currentStep === 'step1' && results.step1) {
      content = results.step1;
    } else if (results.step2) {
      content = `${results.step2.content}\n\n${results.step2.hashtags}`;
    }
    navigator.clipboard.writeText(content);
  };

  const getFormattedContent = () => {
    if (results.research && results.sequence) {
      // Handle email/DM sequence case
      if (currentStep === 'step1' && results.research) {
        return formatResearch(results.research);
      } else if (results.sequence) {
        return formatSequence(results.sequence);
      }
    }
    // Handle social media post case
    return formatSocialMediaPost();
  };

  const getStepTitle = () => {
    if (results.research && results.sequence) {
      if (currentStep === 'step1') {
        try {
          const data = JSON.parse(results.research);
          return `Industry Analysis: ${data?.industry || ''}`;
        } catch {
          return 'Industry Analysis';
        }
      }
      return messageType === 'LinkedIn DM' ? 'LinkedIn Campaign' : 'Email Campaign';
    }
    return currentStep === 'step1' ? 'Platform Strategy' : 'Social Media Post';
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-secondary/20 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary/20">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-semibold text-text-primary">
              {getStepTitle()}
            </h2>
            <div className="flex items-center gap-2 text-text-secondary text-sm">
              <div className={`w-2 h-2 rounded-full ${currentStep === 'step1' ? 'bg-primary' : 'bg-secondary/20'}`} />
              <span>Step {currentStep === 'step1' ? '1/2' : '2/2'}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary/10 rounded-lg transition-colors"
          >
            <FiX className="w-6 h-6 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose prose-invert prose-h1:text-primary prose-h2:text-text-primary prose-h3:text-text-primary prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {getFormattedContent()}
            </ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-secondary/20">
          <div className="flex justify-between items-center">
            {currentStep === 'step2' && (
              <button
                onClick={() => setCurrentStep('step1')}
                className="flex items-center gap-2 px-6 py-3 rounded-lg border border-secondary/20 hover:bg-secondary/10 transition-colors"
              >
                <FiArrowLeft className="w-5 h-5" />
                Back to {results.research ? 'Research' : 'Strategy'}
              </button>
            )}
            <div className="flex gap-4 ml-auto">
              {currentStep === 'step1' && (
                <button
                  onClick={() => setCurrentStep('step2')}
                  className="flex items-center gap-2 btn-primary"
                >
                  View {results.research ? 
                    (messageType === 'LinkedIn DM' ? 'LinkedIn Campaign' : 'Email Campaign') : 
                    'Social Media Post'}
                  <FiArrowRight className="w-5 h-5" />
                </button>
              )}
              <button 
                onClick={handleCopyToClipboard}
                className="btn-primary"
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsModal; 